import React, { Suspense } from 'react';
import { observer } from 'mobx-react';
import { RouterView, RouterStore } from '@estee/elc-router';
import {
  DEFAULT,
  DELIVERY,
  NOT_FOUND,
  PAYMENT,
  INCOMPATIBLE_BROWSER,
} from './routes';
import { diContainer, serviceNames } from './diContainer/diContainer';
import AppViewController from './controllers/AppViewController';
import { TIMEOUT_INTERVAL } from './constants/Constants';
import TranslationContext from './hocs/withTranslations/TranslationContext';
import withTranslations from './hocs/withTranslations';
import { enhanceWithErrorCapture } from './application/services/LogServiceError';

// preload the screens
// NOTE: trailing commas are not allowed with dynamic imports,
// so I was forced to use long lines for import statements
setTimeout(() => {
  // eslint-disable-next-line max-len
  import(/* webpackChunkName: "DeliveryScreen", webpackPreload: true */ './screens/DeliveryScreen');
  // eslint-disable-next-line max-len
  import(/* webpackChunkName: "PaymentScreen", webpackPreload: true */ './screens/PaymentScreen');
  // eslint-disable-next-line max-len
  import(/* webpackChunkName: "IncompatibleBrowserScreen", webpackPreload: true */ './screens/IncompatibleBrowserScreen');
}, TIMEOUT_INTERVAL.NULL);

const DeliveryScreen = React.lazy(() => import('./screens/DeliveryScreen'));
const PaymentScreen = React.lazy(() => import('./screens/PaymentScreen'));
const IncompatibleBrowserScreen = React.lazy(() => import('./screens/IncompatibleBrowserScreen'));

const ErrorBoundary = withTranslations(({ children }) => <>{children}</>);

@observer
class App extends React.Component {
  private ctrl: AppViewController;

  private stateRouter: RouterStore;

  constructor(props: {}) {
    super(props);

    this.ctrl = diContainer.get(serviceNames.appViewController);
    this.stateRouter = diContainer.get(serviceNames.stateRouter);
  }

  @enhanceWithErrorCapture
  async componentDidMount(): Promise<void> {
    await this.ctrl.loaded();

    this.ctrl.setInitialSameAsShipping();
    this.ctrl.setInitialIsCompanyBilling();
  }

  render(): React.ReactNode {
    const {
      data: {
        isLoading,
        translationSchema,
      },
    } = this.ctrl;

    const viewMap = {
      [DEFAULT.ROUTE]: <></>,
      [DELIVERY.ROUTE]: <DeliveryScreen
        setFocusBillingSection={this.ctrl.setFocusBillingSection}
        navigateToBilling={this.ctrl.navigateToBilling}
        showUpsWarningMessage={this.ctrl.showUpsWarningMessage}
        focusBillingSection={this.ctrl.focusBillingSection}
        handleUpsSelect={this.ctrl.handleUpsSelect}
        showUpsAddressValidationModal={this.ctrl.showUpsAddressValidationModal}
        showUpsAddressValidation={this.ctrl.isUPSModalActive}
        isLoading={isLoading}
        isBillingSameAsShipping={this.ctrl.isBillingSameAsShipping}
        toggleSameAsShipping={this.ctrl.toggleSameAsShipping}
        isCompanyBilling={this.ctrl.isCompanyBilling}
        toggleCompanyBilling={this.ctrl.toggleCompanyBilling}
      />,
      [PAYMENT.ROUTE]: <PaymentScreen
        navigateToBilling={this.ctrl.navigateToBilling}
        navigateToStore={this.ctrl.navigateToStore}
        showUpsWarningMessage={this.ctrl.isWarningMessageActive}
        isLoading={isLoading}
        isCompanyBilling={this.ctrl.isCompanyBilling}
      />,
      [INCOMPATIBLE_BROWSER.ROUTE]: <IncompatibleBrowserScreen />,
      [NOT_FOUND.ROUTE]: <div>not found</div>,
    };
    
    return (
      <TranslationContext.Provider value={translationSchema}>
        <ErrorBoundary>
          <Suspense fallback={<></>}>
            <RouterView
              routerStore={this.stateRouter.mobxStateStore}
              viewMap={viewMap}
            />
          </Suspense>
        </ErrorBoundary>
      </TranslationContext.Provider>
    );
  }
}

export default App;
